import { ODDS_FORMAT, type OddsFormat } from '~/vendor/cx-web/domain-types';

/**
 * Cookie bridge between legacy UK (`oddschecker-frontend`) and cx-web, for the duration of the UK
 * migration and no longer.
 *
 * The two stacks store the same preference under different names in different words — legacy writes
 * `odds_type=traditional|decimal|us`, cx-web writes `odds_format=fractional|decimal|american`. UK
 * pages move to cx-web one at a time, so a visitor crosses between the two mid-session and the
 * preference has to survive the crossing in both directions (OCGC-3885). Legacy is not modified: on
 * OC, cx-web reads legacy's cookie and writes both, which also leaves `odds_format` correct for the
 * day legacy is switched off.
 *
 * Delete this module and its call sites once legacy UK is gone. Nothing outside OC reads it.
 */

/** Legacy's cookie. Only OC has one. */
export const LEGACY_ODDS_TYPE_COOKIE = 'odds_type';
/** cx-web's own cookie, on every repub. */
export const ODDS_FORMAT_COOKIE = 'odds_format';

/**
 * A year, matching legacy's own expiry. cx-web's non-OC default is a day; keeping that on OC would
 * let `odds_format` lapse while `odds_type` lived on, and the next reconcile would silently rewrite
 * it from legacy — correct, but it would look like the preference had been forgotten and restored.
 */
export const LEGACY_UK_COOKIE_MAX_AGE_MS = 365 * 24 * 60 * 60 * 1000;

const LEGACY_UK_REPUB = 'OC';

const FORMAT_BY_LEGACY_VALUE: Record<string, OddsFormat> = {
  decimal: ODDS_FORMAT.DECIMAL,
  traditional: ODDS_FORMAT.FRACTIONAL,
  us: ODDS_FORMAT.AMERICAN,
};

/**
 * Total on purpose. Legacy's XSL matches `odds_type` against an exact list and renders an EMPTY odds
 * grid for anything else — it has no default — so a new member of `ODDS_FORMAT` must fail to compile
 * here rather than start writing a value that blanks the page for everyone still on legacy.
 */
const LEGACY_VALUE_BY_FORMAT: Record<OddsFormat, string> = {
  [ODDS_FORMAT.AMERICAN]: 'us',
  [ODDS_FORMAT.DECIMAL]: 'decimal',
  [ODDS_FORMAT.FRACTIONAL]: 'traditional',
  [ODDS_FORMAT.TRADITIONAL]: 'traditional',
};

const RENDERABLE_FORMATS = new Set<string>([ODDS_FORMAT.AMERICAN, ODDS_FORMAT.DECIMAL, ODDS_FORMAT.FRACTIONAL]);

/**
 * Values legacy's XSL renders that cx-web has no format for, so they are left ALONE rather than
 * converged. `winnings` shows the return on a stake — the price multiplied by legacy's companion
 * `odds_default_stake` cookie — so there is no cx-web format to map it onto.
 */
const LEGACY_ONLY_ODDS_TYPES = new Set(['winnings']);

/** Whether legacy renders this `odds_type` but cx-web has no format for it. */
export function isLegacyOnlyOddsType(value: string | undefined): boolean {
  return !!value && LEGACY_ONLY_ODDS_TYPES.has(value);
}

/**
 * Every `odds_type` value in a raw `Cookie:` request header, in the order the browser sent them.
 *
 * More than one means the visitor is carrying two `odds_type` cookies on different `Domain`s, which
 * no ordinary cookie API can see: `Cookie:` carries names and values only, so `Astro.cookies.get`
 * returns whichever came first and reports nothing unusual. Values are left encoded, matching Astro's
 * own parse (`{ decode: identity }`), so this and `Astro.cookies.get` never disagree about a value.
 */
export function readLegacyOddsTypeCookies(cookieHeader: string | null | undefined): string[] {
  if (!cookieHeader) return [];

  return cookieHeader
    .split(';')
    .map(pair => pair.trim())
    .filter(pair => pair.startsWith(`${LEGACY_ODDS_TYPE_COOKIE}=`))
    .map(pair => pair.slice(LEGACY_ODDS_TYPE_COOKIE.length + 1));
}

/** Whether this repub shares its odds preference with legacy UK. */
export function usesLegacyOddsCookie(repub: string | undefined): boolean {
  return repub === LEGACY_UK_REPUB;
}

/** Legacy's word for a format, or `undefined` if it is not one legacy uses. */
export function fromLegacyOddsType(value: string | undefined): OddsFormat | undefined {
  return value ? FORMAT_BY_LEGACY_VALUE[value] : undefined;
}

/**
 * Our word for a format in legacy's vocabulary. `undefined` only for a value that is not really an
 * `OddsFormat` — the callers skip the write rather than blanking legacy's odds grid.
 */
export function toLegacyOddsType(format: OddsFormat): string | undefined {
  return LEGACY_VALUE_BY_FORMAT[format];
}

/** A cx-web cookie value, with legacy's `traditional` synonym folded in. `undefined` if unusable. */
export function normaliseOddsFormat(value: string | undefined): OddsFormat | undefined {
  const normalised = value === ODDS_FORMAT.TRADITIONAL ? ODDS_FORMAT.FRACTIONAL : value;
  return RENDERABLE_FORMATS.has(normalised ?? '') ? (normalised as OddsFormat) : undefined;
}

export interface LegacyUkCookieWrite {
  name: string;
  /**
   * `shared` carries the parent `Domain` so legacy — which may be served from a different host under
   * the same domain — overwrites the one cookie instead of gaining a second. `host` is how cx-web has
   * always written `odds_format`; widening it now would orphan every existing host-only copy and the
   * browser would send both, letting the stale one win by ordering.
   *
   * A `shared` write must match legacy's own `Domain` and `Path` or the browser keeps two `odds_type`
   * cookies rather than one, and the stale one wins — both stacks read the first occurrence. Callers
   * get `Domain`/`Secure` from `resolveLegacyUkCookieScope`, which derives the domain from the host
   * exactly as legacy does; `Path=/`, `SameSite=Lax` and a year's expiry are fixed at the call site.
   */
  scope: 'host' | 'shared';
  value: string;
}

export interface LegacyUkOddsFormatInput {
  /** cx-web's `odds_format` cookie, if the visitor has one. */
  currentValue: string | undefined;
  /** Legacy's `odds_type` cookie, if the visitor has one. */
  legacyValue: string | undefined;
  /** What this repub actually offers — `getAvailableOddsFormats(repub, siteConfig.oddsFormat)`. */
  offeredFormats: OddsFormat[];
  /** Repub default, used when neither cookie yields an offered format. */
  siteDefault: OddsFormat;
}

export interface LegacyUkOddsFormatResolution {
  /** Cookies to write on this response so both stacks agree. Empty when they already do. */
  cookieWrites: LegacyUkCookieWrite[];
  /** `odds_type` held a format only legacy can render, so it was left as it was. */
  legacyOnlyValue: boolean;
  /** `odds_type` held a value NEITHER stack's vocabulary contains, so it was ignored. */
  legacyValueUnknown: boolean;
  oddsFormat: OddsFormat;
  /**
   * No stored cookie yielded a format this repub offers, so the visitor's choice could not be
   * honoured and the page renders the default. False when either cookie was usable — that one is
   * used and nothing is lost.
   *
   * It says nothing about which cookies were then rewritten: `winnings` is left in place even here
   * (see `legacyOnlyValue`), so a `winnings` visitor whose `odds_format` is also unofferable has only
   * `odds_format` converged.
   */
  preferenceNotOffered: boolean;
}

/**
 * Resolve the odds format for an OC page view from both cookies, and say which of them need
 * rewriting so the next request — to either stack — reads the same answer.
 *
 * Legacy wins when the two disagree: everything not yet migrated is still served by legacy, so its
 * cookie is the more recent record of what the visitor picked. It wins only among formats this repub
 * actually offers, though — the site default is the last resort, not the answer to legacy naming
 * something unrenderable here.
 */
export function reconcileLegacyUkOddsFormat({
  currentValue: rawCurrentValue,
  legacyValue: rawLegacyValue,
  offeredFormats,
  siteDefault,
}: LegacyUkOddsFormatInput): LegacyUkOddsFormatResolution {
  // `odds_type=` is a cookie with no preference in it, and must count as absent rather than as
  // present-but-unusable, or the write guard below fires for a visitor who never chose anything.
  const currentValue = rawCurrentValue || undefined;
  const legacyValue = rawLegacyValue || undefined;

  const mappedLegacy = fromLegacyOddsType(legacyValue);
  const mappedCurrent = normaliseOddsFormat(currentValue);
  const legacyOnlyValue = isLegacyOnlyOddsType(legacyValue);
  const legacyValueUnknown = !!legacyValue && !mappedLegacy && !legacyOnlyValue;

  // Legacy wins, but only where it names a format this repub offers. Taking legacy's answer first and
  // checking it afterwards would throw away a perfectly good `odds_format` — see the test.
  const isOffered = (format: OddsFormat | undefined): format is OddsFormat =>
    !!format && offeredFormats.includes(format);
  const preference = [mappedLegacy, mappedCurrent].find(isOffered);
  const preferenceNotOffered = (!!mappedLegacy || !!mappedCurrent) && !preference;
  const fallback = offeredFormats.includes(siteDefault) ? siteDefault : (offeredFormats[0] ?? siteDefault);
  const oddsFormat = preference ?? fallback;

  // A visitor holding neither cookie has expressed no preference. Planting one on a plain page view
  // would put every first-time UK visitor into the odds-format leg of the CDN cache key for nothing.
  const hasStoredPreference = legacyValue !== undefined || currentValue !== undefined;
  const cookieWrites: LegacyUkCookieWrite[] = [];

  if (hasStoredPreference) {
    const legacyTarget = toLegacyOddsType(oddsFormat);
    // `legacyOnlyValue` guards a SILENT back-fill only. An explicit dropdown pick still overwrites
    // `odds_type`, in changeOddsFormat — that is the visitor choosing, not us guessing for them.
    if (legacyTarget && legacyTarget !== legacyValue && !legacyOnlyValue) {
      cookieWrites.push({ name: LEGACY_ODDS_TYPE_COOKIE, scope: 'shared', value: legacyTarget });
    }
    if (oddsFormat !== currentValue) {
      cookieWrites.push({ name: ODDS_FORMAT_COOKIE, scope: 'host', value: oddsFormat });
    }
  }

  return { cookieWrites, legacyOnlyValue, legacyValueUnknown, oddsFormat, preferenceNotOffered };
}
